import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { supabase } from "@/integrations/supabase/client";
import { Check, X, Star, MapPin } from "lucide-react";
import heroImg from "@/assets/hero.webp";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About Creed Car Rental | Boutique Luxury Rentals in Miami" },
      { name: "description", content: "Meet Creed Car Rental — Miami's boutique luxury car rental. Owner-run, hand-picked exotics & SUVs, no middleman, no hidden fees. Insurance always included." },
      { property: "og:title", content: "About Creed Car Rental | Boutique Luxury Rentals in Miami" },
      { property: "og:description", content: "Owner-run boutique luxury rental in Miami. Insurance included, delivery to your hotel or Airbnb." },
      { property: "og:url", content: "https://creedcarental.com/about" },
      { property: "og:image", content: heroImg },
      { name: "twitter:image", content: heroImg },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/about" }],
  }),
  component: About,
});

type Settings = {
  trips_count: number | null;
  avg_rating: number | null;
  years_in_business: number | null;
  founder_name: string | null;
  founder_role: string | null;
  founder_bio: string | null;
  founder_photo_url: string | null;
  service_areas: string | null;
};

type Testimonial = {
  id: string;
  author: string;
  author_role: string | null;
  quote: string;
  rating: number;
};

function About() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    supabase.from("settings").select("trips_count,avg_rating,years_in_business,founder_name,founder_role,founder_bio,founder_photo_url,service_areas").eq("id", 1).maybeSingle().then(({ data }) => setSettings(data as Settings | null));
    supabase.from("testimonials").select("id,author,author_role,quote,rating").eq("active", true).eq("approved", true).order("sort_order").then(({ data }) => setTestimonials((data as Testimonial[]) || []));
  }, []);

  const stats = settings ? [
    settings.trips_count ? { value: `${settings.trips_count}+`, label: "Trips delivered" } : null,
    settings.avg_rating ? { value: `${settings.avg_rating}★`, label: "Average rating" } : null,
    settings.years_in_business ? { value: `${settings.years_in_business} yrs`, label: "In Miami" } : null,
  ].filter(Boolean) as { value: string; label: string }[] : [];

  const areas = settings?.service_areas?.split("\n").map(a => a.trim()).filter(Boolean) || [];

  return (
    <div className="min-h-screen bg-cream">
      <SiteHeader />

      {/* Hero header */}
      <section className="relative isolate overflow-hidden border-b border-border/60 bg-espresso py-20 md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 80% at 85% 20%, oklch(0.72 0.15 65 / 0.35), transparent 60%), radial-gradient(50% 70% at 10% 90%, oklch(0.72 0.15 65 / 0.18), transparent 60%)",
          }}
          aria-hidden
        />
        <svg
          className="pointer-events-none absolute -right-24 -top-24 h-[520px] w-[520px] text-accent/20"
          viewBox="0 0 600 600"
          fill="none"
          aria-hidden
        >
          <circle cx="300" cy="300" r="220" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="160" stroke="currentColor" strokeWidth="1" />
          <circle cx="300" cy="300" r="100" stroke="currentColor" strokeWidth="1" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-accent">About</div>
          <h1 className="mt-4 font-display text-5xl text-cream md:text-6xl">Built in Miami. One car at a time.</h1>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-20">
        <p className="mt-6 text-lg text-espresso/80 leading-relaxed">
          Creed Car Rental didn't start as a business — it started as a favor. One car, lent to a friend in town for the weekend, became a second car, then a third, then a phone that wouldn't stop ringing during F1 weekend. Forty-plus trips later, it's a real fleet, still run by one person who answers his own messages.
        </p>
        <p className="mt-4 text-lg text-espresso/80 leading-relaxed">
          Our guests come down for the things Miami does best: bachelor and bachelorette weekends in South Beach, anniversaries at Carbone, F1 weekend at the Hard Rock, Art Basel, and execs who'd rather pull up to Brickell in something memorable instead of a beige sedan from the airport counter.
        </p>
        <p className="mt-4 text-lg text-espresso/80 leading-relaxed">
          No call centers. No counter upsells. No surprise fees at return. Just the right car, delivered to your hotel or Airbnb, with insurance and a real phone number that rings the owner.
        </p>

        {stats.length > 0 && (
          <div className="mt-10 grid grid-cols-3 gap-3 rounded-lg border border-border bg-card p-6">
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <div className="font-display text-3xl text-accent">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-espresso/60">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {settings?.founder_name && (
          <div className="mt-14 rounded-lg border border-border bg-card p-6">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">Meet the owner</div>
            <div className="mt-4 flex flex-col gap-5 sm:flex-row sm:items-start">
              {settings.founder_photo_url && (
                <img src={settings.founder_photo_url} alt={settings.founder_name} className="h-24 w-24 shrink-0 rounded-full object-cover" />
              )}
              <div>
                <div className="font-display text-2xl text-espresso">{settings.founder_name}</div>
                {settings.founder_role && <div className="text-sm text-espresso/60">{settings.founder_role}</div>}
                {settings.founder_bio && <p className="mt-3 text-espresso/80 leading-relaxed whitespace-pre-line">{settings.founder_bio}</p>}
              </div>
            </div>
          </div>
        )}

        {areas.length > 0 && (
          <div className="mt-14">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">Service area</div>
            <h2 className="mt-2 font-display text-3xl text-espresso">Where we deliver</h2>
            <p className="mt-3 text-espresso/70">Complimentary delivery in Miami-Dade. Surrounding areas available on request.</p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2">
              {areas.map(a => (
                <li key={a} className="flex items-center gap-2 text-espresso/80">
                  <MapPin className="h-4 w-4 text-accent shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-14 rounded-lg border border-border bg-card p-6">
          <div className="font-display text-xl text-espresso">What's included</div>
          <ul className="mt-3 grid gap-2 text-sm text-espresso/80 sm:grid-cols-2">
            <li>✓ Comprehensive insurance</li>
            <li>✓ 24/7 roadside assistance</li>
            <li>✓ Delivery across Miami-Dade</li>
            <li>✓ Detailed &amp; sanitized vehicle</li>
          </ul>
          <div className="mt-5 font-display text-xl text-espresso">What's extra</div>
          <ul className="mt-3 grid gap-2 text-sm text-espresso/80 sm:grid-cols-2">
            <li>· Tolls &amp; traffic tickets</li>
            <li>· Cleaning fee (if returned dirty)</li>
            <li>· Late return fees</li>
          </ul>
        </div>

        <div className="mt-14">
          <div className="text-sm uppercase tracking-[0.25em] text-accent">Why Creed</div>
          <h2 className="mt-2 font-display text-3xl text-espresso">Creed vs. the big platforms</h2>
          <div className="mt-6 overflow-hidden rounded-lg border border-border bg-card">
            <div className="grid grid-cols-3 text-sm">
              <div className="bg-secondary/50 p-4 font-semibold text-espresso">&nbsp;</div>
              <div className="bg-accent/10 p-4 font-semibold text-accent text-center border-x border-accent/30">Creed Car Rental</div>
              <div className="bg-secondary/50 p-4 font-semibold text-espresso text-center">Turo / Hertz</div>

              <Row label="Insurance included" creed back="Often extra" />
              <Row label="Direct owner contact" creed back="Platform support" />
              <Row label="Delivery to hotel / Airbnb" creed back="Limited or fee" />
              <Row label="Upfront, all-in quotes" creed back="Often +20–40% at checkout" />
              <Row label="Same vehicle as advertised" creed back="Sometimes swapped" lastRow />
            </div>
          </div>
        </div>

        {testimonials.length > 0 && (
          <div className="mt-14">
            <div className="text-sm uppercase tracking-[0.25em] text-accent">Guests</div>
            <h2 className="mt-2 font-display text-3xl text-espresso">What they say</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {testimonials.map(t => (
                <figure key={t.id} className="rounded-lg border border-border bg-card p-5">
                  <div className="flex">{Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < t.rating ? "fill-accent text-accent" : "text-muted-foreground/30"}`} />
                  ))}</div>
                  <blockquote className="mt-3 text-espresso/85 leading-relaxed">"{t.quote}"</blockquote>
                  <figcaption className="mt-3 text-sm text-espresso">
                    <span className="font-medium">{t.author}</span>
                    {t.author_role && <span className="text-espresso/60"> · {t.author_role}</span>}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}

        <div className="mt-14 rounded-lg border border-border bg-card p-8 text-center">
          <p className="font-display text-2xl text-espresso">Ready when you are.</p>
          <p className="mt-2 text-espresso/70">Pick a car, send your dates, we'll handle the rest.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/fleet" className="rounded-md bg-primary px-6 py-3 text-primary-foreground font-medium hover:bg-primary/90">Browse the fleet</Link>
            <Link to="/contact" className="rounded-md border border-border px-6 py-3 text-espresso hover:border-accent">Contact us</Link>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function Row({ label, creed, no, back, lastRow }: { label: string; creed?: boolean; no?: boolean; back: string; lastRow?: boolean }) {
  const border = lastRow ? "" : "border-t border-border";
  return (
    <>
      <div className={`${border} p-4 text-espresso`}>{label}</div>
      <div className={`${border} p-4 text-center`}>
        {creed ? <Check className="inline h-5 w-5 text-accent" /> : <X className="inline h-5 w-5 text-muted-foreground" />}
      </div>
      <div className={`${border} p-4 text-center text-muted-foreground text-xs`}>
        {no ? <X className="inline h-5 w-5" /> : back}
      </div>
    </>
  );
}
